import { ArrowRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';

import {
  createIntentKey,
  listActivity,
  useRequestSynchronizationMutation,
  type ActivityItem,
} from '../../api/index.js';
import { Button, StateSurface, StatusPill } from '../../design-system/index.js';
import { formatTime, PageHeader } from '../shared/page.js';

export function ActivityPage() {
  const activity = useInfiniteQuery({
    queryKey: ['activity', { limit: 50 }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listActivity({ limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    refetchInterval: () =>
      document.visibilityState === 'visible' && navigator.onLine ? 15_000 : false,
  });
  const retry = useRequestSynchronizationMutation();
  const items = activity.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div>
      <PageHeader title="Activity" />
      {activity.isLoading ? (
        <StateSurface kind="loading" title="Loading activity" />
      ) : activity.error ? (
        <StateSurface
          kind="error"
          title="Activity unavailable"
          action={
            <Button variant="secondary" onClick={() => void activity.refetch()}>
              Try again
            </Button>
          }
        />
      ) : !items.length ? (
        <StateSurface title="No activity yet">
          Generation and catalog activity will appear here.
        </StateSurface>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          {items.map((item) => {
            const onRetry =
              item.kind === 'synchronization' && item.actions.includes('retry')
                ? () =>
                    void retry
                      .mutateAsync({
                        connectionId: item.connection_id,
                        body: { synchronization_kind: item.synchronization_kind },
                        idempotencyKey: createIntentKey(),
                      })
                      .then(() => activity.refetch())
                : undefined;
            return (
              <ActivityRow
                key={`${item.kind}-${item.id}`}
                item={item}
                pending={retry.isPending}
                {...(onRetry ? { onRetry } : {})}
              />
            );
          })}
        </div>
      )}
      {activity.hasNextPage ? (
        <div className="mt-6 flex justify-center">
          <Button
            variant="secondary"
            disabled={activity.isFetchingNextPage}
            onClick={() => void activity.fetchNextPage()}
          >
            {activity.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
      {retry.error ? (
        <p className="mt-4 text-sm text-err" role="alert">
          Synchronization could not be requested. Try again.
        </p>
      ) : null}
    </div>
  );
}

function ActivityRow({
  item,
  onRetry,
  pending,
}: {
  item: ActivityItem;
  onRetry?: () => void;
  pending: boolean;
}) {
  const title = activityTitle(item);
  const href =
    item.kind === 'generation'
      ? `/generations/${item.id}`
      : item.kind === 'favorite' || item.kind === 'library_import'
        ? `/tracks/${item.track_id}`
        : item.kind === 'delivery'
          ? `/deliveries/${item.id}`
          : null;
  return (
    <article className="grid gap-3 border-b border-border p-4 last:border-b-0 sm:grid-cols-[8rem_minmax(0,1fr)_auto] sm:items-center sm:gap-5">
      <span className="justify-self-start rounded-full border border-border bg-bg-2 px-2.5 py-1 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-ink-2">
        {item.kind.replaceAll('_', ' ')}
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-medium text-ink">{title}</p>
          <StatusPill status={item.status} />
        </div>
        {item.failure ? (
          <p className="mt-1 text-xs text-err">
            {item.failure.message}
            {item.failure.action_required ? ` · ${item.failure.action_required}` : ''}
          </p>
        ) : null}
        <p className="mt-1 font-mono text-xs text-ink-3">{formatTime(item.occurred_at)}</p>
      </div>
      <div className="flex items-center gap-2 sm:justify-end">
        {onRetry ? (
          <Button size="small" variant="secondary" disabled={pending} onClick={onRetry}>
            <RefreshCw />
            Retry
          </Button>
        ) : null}
        {href ? (
          <Button asChild size="small" variant="ghost">
            <Link to={href}>
              Open <ArrowRight />
            </Link>
          </Button>
        ) : null}
      </div>
    </article>
  );
}

function activityTitle(item: ActivityItem) {
  switch (item.kind) {
    case 'generation':
      return `${item.output_count} ${item.output_count === 1 ? 'output' : 'outputs'}`;
    case 'favorite':
      return `${item.display_title ?? 'Track'} · ${item.desired_value ? 'liked' : 'unliked'}`;
    case 'library_import':
      return `${item.display_title ?? 'Track'} · owned WAV`;
    case 'delivery':
      return item.destination_name ? `Delivery to ${item.destination_name}` : 'External delivery';
    case 'synchronization':
      return `${item.synchronization_kind === 'favorites' ? 'Favorite' : 'Catalog'} synchronization · attempt ${item.attempt_count}`;
  }
}
